'use client'

import Image from 'next/image'
import {FC} from 'react'

import {AboutUsStatistics} from '@/components/about-us/AboutUsStatistics'
import {ModernLegalServices} from '@/components/about-us/ModernLegalServices'
import {StoryTimeline} from '@/components/about-us/StoryTimeline'
import {BrandGrowthBanner} from '@/components/common/banner/BrandGrowthBanner'
import {Section} from '@/components/common/content/Section'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Hero} from '@/components/common/hero/Hero'
import {TramaInMedia} from '@/components/common/media/TramaInMedia'
import {OfficeLocations} from '@/components/common/office-locations/OfficeLocations'
import {Team} from '@/components/common/team/Team'
import {Page} from '@/components/Page'
import {TrustpilotCarousel} from '@/components/trustpilot/TrustpilotCarousel'
import {getAboutUsContent, getAboutUsStatistics} from '@/content/aboutUs'
import {useTranslation} from '@/i18n'
import {CONTAINER_PADDING, CONTAINER_PADDING_MOBILE, CONTAINER_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {useLanguage} from '@/utils/i18n/language'
import {AboutUsStatisticName, APPROX_REGISTERED_TRADEMARKS} from '@/utils/statistics/constants'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

const AboutUs: FC = () => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {header, hero, getStarted} = getAboutUsContent()
  const isEnLanguage = language === Locale.en
  const aboutUsStatistics = getAboutUsStatistics(APPROX_REGISTERED_TRADEMARKS, true)

  const selectedStatistics = [
    aboutUsStatistics[AboutUsStatisticName.registeredTrademarks],
    aboutUsStatistics[AboutUsStatisticName.settledCases],
    aboutUsStatistics[AboutUsStatisticName.coveredCountries],
    aboutUsStatistics[AboutUsStatisticName.teamMembers],
  ]

  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () => analytics.track('Click on How we work (About us - Header)'),
        }}
      >
        <Hero
          title={t(hero.title)}
          backgroundColor={theme.palette.background.default}
          isCentered
          isFullWidth
        >
          <div className="image">
            <Image
              src={hero.imageSrc}
              alt={hero.imageAlt}
              width={1680}
              height={810}
              priority
              fetchPriority="high"
              sizes={`${MOBILE_SCREENSIZE} calc(100vw - ${CONTAINER_PADDING_MOBILE} * 2), ${TABLET_SCREENSIZE} calc(100vw - ${CONTAINER_PADDING} * 2), ${CONTAINER_WIDTH}`}
              style={{display: 'block', width: '100%', height: 'auto'}}
            />
          </div>
          <div className="subtitle">
            <Text
              variant={TextVariant.text24}
              fontWeight={TextFontWeight.light}
              color={theme.palette.text.light}
            >
              {t(hero.subtitle)}
            </Text>
          </div>
        </Hero>
        <Section>
          <AboutUsStatistics statistics={selectedStatistics} />
        </Section>
        <Section hasPaddingTop>
          <Team />
        </Section>
        <Section hasPaddingTop>
          <StoryTimeline />
        </Section>
        <Section hasPaddingTop>
          <ModernLegalServices />
        </Section>
        <Section hasPaddingTop>
          <OfficeLocations />
        </Section>
        <Section hasPaddingTop hasPaddingBottom>
          {isEnLanguage ? <TrustpilotCarousel isCentered /> : <TramaInMedia />}
        </Section>
        <Section hasPaddingTop hasPaddingBottom backgroundColor={theme.palette.background.butter}>
          <BrandGrowthBanner
            title={t(getStarted.title)}
            buttonLinkHref={getStarted.buttonLinkHref}
            buttonLinkText={t(getStarted.buttonLinkText)}
            onClick={() => analytics.track('BrandGrowthBanner-AboutUs')}
          />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          margin: ${theme.spacing[6]} 0;
        }

        .subtitle {
          max-width: 940px;
          margin: 0 auto;
        }
      `}</style>
    </>
  )
}

export default AboutUs
